import React, { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { Box, Button, Chip, Paper, TextField, Typography } from "@mui/material";
import { useCreatePlanOnly, useGetAllExercises } from "../../service/api";
import { PlanExercisePickerDialog } from "../plan-exercise-picker-dialog";

const EMPTY_PLAN = {
  name: "",
  description: "",
  category: "",
  level: "",
  equipment: "",
  depth: "",
  durationInSeconds: 0,
};

export const PlanForm = () => {
  const { t } = useTranslation();
  const { mutate: createPlan, isPending: creatingPlan } = useCreatePlanOnly();
  const { data: exercises = [] } = useGetAllExercises();

  const [planData, setPlanData] = useState(EMPTY_PLAN);
  const [selectedExerciseIds, setSelectedExerciseIds] = useState<string[]>([]);
  const [pickerOpen, setPickerOpen] = useState(false);

  useEffect(() => {
    const total = selectedExerciseIds.reduce((sum, id) => {
      const ex = exercises.find((e) => e.id === id);
      return sum + (ex?.durationInSeconds ?? 0);
    }, 0);
    setPlanData((prev) => ({ ...prev, durationInSeconds: total }));
  }, [selectedExerciseIds, exercises]);

  const handlePlanChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    const { name, value } = e.target;
    setPlanData((prev) => ({ ...prev, [name]: name === "durationInSeconds" ? Number(value) : value }));
  };

  const handleCreatePlan = (e: React.FormEvent) => {
    e.preventDefault();
    createPlan(
      { ...planData, exercisesIds: selectedExerciseIds },
      { onSuccess: () => { setPlanData(EMPTY_PLAN); setSelectedExerciseIds([]); } }
    );
  };

  return (
    <Paper elevation={2} sx={{ p: 2, backgroundColor: "#fff", color: "#000" }}>
      <PlanExercisePickerDialog
        open={pickerOpen}
        initialSelectedIds={selectedExerciseIds}
        onClose={() => setPickerOpen(false)}
        onConfirm={(ids) => { setSelectedExerciseIds(ids); setPickerOpen(false); }}
      />

      <Typography variant="subtitle1" fontWeight="bold" mb={1}>{t("videoLibrary.planForm.heading")}</Typography>
      <Box component="form" onSubmit={handleCreatePlan} sx={{ display: "flex", flexDirection: "column", gap: 1 }}>
        <Box sx={{ display: "flex", gap: 1 }}>
          <TextField name="name" label={t("videoLibrary.common.planNameField")} value={planData.name} onChange={handlePlanChange} required size="small" sx={{ flex: 2 }} />
          <TextField name="durationInSeconds" label={t("videoLibrary.common.durationSecondsField")} type="number" value={planData.durationInSeconds} onChange={handlePlanChange} onFocus={(e) => e.target.select()} required size="small" sx={{ flex: 1 }} />
        </Box>
        <TextField name="description" label={t("videoLibrary.common.descriptionField")} value={planData.description} onChange={handlePlanChange} required size="small" />
        <Box sx={{ display: "flex", gap: 1 }}>
          <TextField name="category" label={t("videoLibrary.common.categoryField")} value={planData.category} onChange={handlePlanChange} required size="small" sx={{ flex: 1 }} />
          <TextField name="level" label={t("videoLibrary.common.levelField")} value={planData.level} onChange={handlePlanChange} required size="small" sx={{ flex: 1 }} />
        </Box>
        <Box sx={{ display: "flex", gap: 1 }}>
          <TextField name="equipment" label={t("videoLibrary.common.equipmentField")} value={planData.equipment} onChange={handlePlanChange} required size="small" sx={{ flex: 1 }} />
          <TextField name="depth" label={t("videoLibrary.common.depthField")} value={planData.depth} onChange={handlePlanChange} required size="small" sx={{ flex: 1 }} />
        </Box>

        <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
          <Button variant="outlined" size="small" onClick={() => setPickerOpen(true)}>
            {t("videoLibrary.common.selectExercises")}
          </Button>
          {selectedExerciseIds.length > 0 ? (
            <Chip label={t("videoLibrary.planForm.selectedCount", { count: selectedExerciseIds.length })} color="primary" size="small" />
          ) : (
            <Typography variant="body2" color="text.secondary">{t("videoLibrary.planForm.noneSelected")}</Typography>
          )}
        </Box>

        <Button type="submit" variant="contained" color="primary" disabled={creatingPlan || selectedExerciseIds.length === 0} sx={{ color: "#fff", "&:hover": { backgroundColor: "#1565c0" } }}>
          {creatingPlan ? t("videoLibrary.planForm.creating") : t("videoLibrary.planForm.submit")}
        </Button>
      </Box>
    </Paper>
  );
};
